import { East } from "@mui/icons-material";
import {
  Box,
  BoxProps,
  Button,
  Container,
  Divider,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import { ReactNode, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import PageSectionBG from "./PageSectionBG";

interface HeadlineSectionProps extends BoxProps {
  dividerTitle?: string;
  title?: string;
  mainContent?: string | ReactNode;
  sx?: SxProps<Theme> | undefined;
  bgcolor?: "light" | "dark";
  titleWidthOverride?: any;
  mainContentWidthOverride?: any;
  ctaButtonText?: string;
  ctaButtonLink?: string;
  dividerButtonText?: string;
  dividerButtonLink?: string;
}

const HeadlineSection = ({
  sx = {},
  title,
  mainContent,
  className,
  dividerTitle,
  bgcolor = "light",
  ctaButtonLink,
  ctaButtonText,
  dividerButtonText,
  dividerButtonLink,
  titleWidthOverride = {
    sm: "100%",
    md: "100%",
    lg: "70%",
    xl: "70%",
  },
  mainContentWidthOverride = {
    sm: "100%",
    md: "100%",
    lg: "45%",
    xl: "45%",
  },
}: HeadlineSectionProps) => {
  const [containerReady, setContainerReady] = useState<boolean>(false);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "start end"],
  });
  const theme = useTheme();

  const marginTop = useTransform(scrollYProgress, [0.5, 1], ["0rem", "8rem"]);

  useEffect(() => {
    if (containerRef.current != null) {
      setContainerReady(true);
    }
  }, [containerRef]);

  return (
    <PageSectionBG
      className={className}
      bgcolor={
        bgcolor === "light"
          ? theme.palette.common.white
          : theme.palette.primary.main
      }
      position={"relative"}
      paddingBottom={0}
      paddingTop={0}
      width={"100%"}
      id={(dividerTitle ? dividerTitle : title ?? "headline-section-divider")
        .toLowerCase()
        .replace(" //", "")
        .replace(/[^\w\s]/gi, "")
        .replace(/ /g, "-")}
    >
      <HeadlineSectionContainer maxWidth="xl" theme={theme} bgcolor={bgcolor}>
        <div ref={containerRef} style={{ width: "100%" }}>
          {containerReady && (
            <Stack
              justifyContent={"start"}
              alignItems={"start"}
              direction={"column"}
              width={"100%"}
              gap={2}
              sx={sx}
            >
              {dividerTitle && (
                <motion.div style={{ marginTop, width: "100%" }}>
                  <Divider
                    className="divider"
                    textAlign="left"
                    sx={{
                      "&:before": { width: 0 },
                      "&::after": {
                        borderColor:
                          bgcolor === "light"
                            ? theme.palette.common.black
                            : theme.palette.common.white,
                        borderWidth: "4px",
                      },
                      position: "relative",
                      width: "100%",
                    }}
                  >
                    <Typography
                      color={
                        bgcolor === "light"
                          ? theme.palette.primary.main
                          : theme.palette.common.white
                      }
                      paddingRight="0.9rem"
                      className="divider-title noselect"
                      variant="textXLBold"
                      ml={-1.5}
                    >
                      {dividerTitle}
                    </Typography>

                    {dividerButtonText && dividerButtonLink && (
                      <Button
                        variant="contained"
                        endIcon={<East />}
                        sx={{
                          background: theme.palette.gradients.wildGreenBoy,
                          color: theme.palette.primary.main,
                          textTransform: "none",
                          position: "absolute",
                          fontWeight: 700,
                          width: "auto",
                          mt: "-0.3rem",
                          right: 0,
                          "&:before": {
                            background:
                              bgcolor === "light"
                                ? theme.palette.common.white
                                : theme.palette.primary.main,
                            position: "absolute",
                            width: "1.5rem",
                            left: "-1.5rem",
                            height: "1rem",
                            content: '""',
                          },
                        }}
                        href={dividerButtonLink}
                        disableElevation
                      >
                        {dividerButtonText}
                      </Button>
                    )}
                  </Divider>
                </motion.div>
              )}

              {title && (
                <motion.div
                  style={{
                    marginTop: dividerTitle === null ? marginTop : 0,
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      typography: {
                        xs: "h2",
                        md: "h3",
                        lg: "xl3",
                        xl: "xl3",
                      },
                      fontWeight: {
                        xs: "700",
                        sm: "700",
                        md: "700",
                        lg: "700",
                      },
                      maxWidth: titleWidthOverride,
                    }}
                    color={
                      bgcolor === "light"
                        ? theme.palette.primary.main
                        : theme.palette.common.white
                    }
                    className="title noselect"
                  >
                    <span
                      style={{ fontWeight: "inherit" }}
                      dangerouslySetInnerHTML={{
                        __html: title,
                      }}
                    ></span>
                  </Typography>
                </motion.div>
              )}

              {mainContent &&
                (typeof mainContent === "string" ? (
                  <Typography
                    sx={{
                      typography: {
                        xs: "h4",
                        sm: "h3",
                        md: "h4",
                        lg: "textXLLight",
                      },
                      fontWeight: {
                        xs: "200",
                        sm: "200",
                        md: "300",
                        lg: "300",
                      },
                      maxWidth: mainContentWidthOverride,
                    }}
                    color={
                      bgcolor === "light"
                        ? theme.palette.primary.main
                        : theme.palette.common.white
                    }
                  >
                    {mainContent}
                  </Typography>
                ) : (
                  <Box component="div" ml={-0.25} width={"100%"}>
                    {mainContent}
                  </Box>
                ))}

              {ctaButtonText && ctaButtonLink && (
                <Button
                  variant="contained"
                  endIcon={<East />}
                  sx={{
                    background: theme.palette.gradients.wildGreenBoy,
                    color: theme.palette.primary.main,
                    textTransform: "none",
                    fontWeight: 700,
                    width: "auto",
                    mt: 1,
                  }}
                  href={ctaButtonLink}
                  disableElevation
                >
                  {ctaButtonText}
                </Button>
              )}
            </Stack>
          )}
        </div>
      </HeadlineSectionContainer>
    </PageSectionBG>
  );
};

export default HeadlineSection;

const HeadlineSectionContainer = styled(Container)<{
  theme: Theme;
  bgcolor: "light" | "dark";
}>`
  position: relative;
  z-index: 1;

  .gradient {
    background-image: ${(props) => props.theme.palette.gradients.wildGreenBoy},
      linear-gradient(
        0deg,
        transparent 10%,
        ${(props) => props.theme.palette.primary.main} 10%,
        ${(props) => props.theme.palette.primary.main} 90%,
        transparent 90%
      );
    padding: ${(props) => (props.bgcolor === "dark" ? "0" : "0 0.6rem")};
    background-clip: text, border-box;
    -webkit-text-fill-color: transparent;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
    line-height: 135%;

    @-moz-document url-prefix() {
      -webkit-text-fill-color: #affa2c;
      background: linear-gradient(
        0deg,
        transparent 10%,
        ${(props) => props.theme.palette.primary.main} 10%,
        ${(props) => props.theme.palette.primary.main} 90%,
        transparent 90%
      );
      color: #affa2c;
    }
  }

  .capitalize {
    text-transform: capitalize;
  }
`;
